import { Component, HostListener, Inject, Renderer2 } from '@angular/core';
import { Router, NavigationEnd, Event as RouterEvent } from '@angular/router';
import { MenuController, Platform, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from './services/localStorage.service';
import * as moment from 'moment';
import { AuthService } from './services/auth.service';
import { AuthGuard } from './services/helpers/auth.guard';
import { DOCUMENT } from '@angular/common';
import { LanguageService } from './services/languageService';
import { environment } from 'src/environments/environment';
import { account } from '.';
import { ProfileUser } from './pages/models/profile-user.interface';
import { ApiService } from './services';
import { first, filter } from 'rxjs';
import { UserDetail } from './models/user-details.model';
import { SearchService } from './services/search.service';
import { UserDetailService } from './services/user-details.service';
import { MenuStateService } from './services/menu-state.service';
import { locale, loadMessages } from 'devextreme/localization';
import { ChangePasswordModalPage } from './pages/change-password-modal/change-password-modal.page';
import { DashboardService } from './services/dashboard.service';
import { Dashboard } from './models/dashboard.model';
import { ThemeService, THEME_PRESETS } from './services/theme.service';
import { RoleManagementService } from './services/role-management.service';
import { AlertService } from './services/alert.service';

// @ts-ignore
const trMessages = require('devextreme/localization/messages/tr.json');

declare global {
  interface Window {
    AsisComponent: any;
    __asisInstance: any;
  }
}

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent {
  /** görünürlük/yerleşim */
  showMenu = true;
  menuSearch = ''; // DevExtreme menü araması
  isReady = false;
  isMobile = false;
  showSearchbar = false;
  isMenuOpen = false; // Desktop'ta menü açık/kapalı durumu
  isMenuCollapsed = false; // Menü daraltılmış durumu (sadece ikonlar)

  /** route state */
  activeUrl = '';
  currentRoute = '';

  /** tema/dil */
  darkModeActivated = false;
  selectedLanguage: string = 'tr';
  availableLanguages: string[] = [];
  public dir: 'rtl' | 'ltr' = 'ltr';
  
  // Theme related properties
  availableThemes = THEME_PRESETS;
  selectedTheme = THEME_PRESETS[0];

  /** kullanıcı */
  isProfileMenuOpen = false;
  profileUser: ProfileUser | null = null;
  environment = environment;
  isAuthenticated = false;
  account = account;
  userDetail: UserDetail | null = null;
  userProfilePictureBase64: string | null = null;

  get profileSubtitle(): string {
    const auth = this.profileUser?.authorities ?? [];
    if (auth.includes('ROLE_ADMIN')) return 'Sistem Yöneticisi';
    if (auth.some(a => a.includes('MANAGER'))) return 'Yönetici';
    if (auth.some(a => a.includes('ANALYST'))) return 'Analist';
    if (auth.some(a => a.includes('USER'))) return 'Kullanıcı';
    return 'Heralp Insight';
  }

  /** MENÜ VERİSİ */
  /** Active menu items from DB, sorted by sortOrder (for sidebar sort sync) */
  private activeMenuItemsSorted: any[] = [];
  /** Last known permission map — kept so dashboard reload can trigger a correct rebuild */
  private _lastPermissions: { [key: string]: any } | undefined;
  /** Start empty — DB-based rebuild fills this as soon as the first API call returns */
  menuItems: any[] = [];
  /** True while menu is being loaded from server — shows skeleton in sidebar */
  menuLoading = false;

  // retry counter for loading dashboards into menu when initial attempt returns empty
  private dashboardMenuRetries = 0;
  private readonly DASHBOARD_MENU_MAX_RETRIES = 3;
  // Cached dynamic dashboard items fetched from API
  private dynamicDashboardItems: any[] = [];

  /** PROFİL MENÜ VERİSİ */
  profileMenuItems = [
    { label: 'topbar.profile', icon: 'person-outline', link: '/profile' },
    { label: 'topbar.settings', icon: 'settings-outline', link: '/settings' },
    {
      label: 'topbar.feedback',
      icon: 'chatbubbles-outline',
      link: '/feedback',
    },
    {
      label: 'topbar.changePassword',
      icon: 'lock-closed-outline',
      action: 'changePassword',
    },
    { label: 'topbar.logout', icon: 'log-out-outline', action: 'logout' },
  ];

  constructor(
    private menuCtrl: MenuController,
    public router: Router, // 👈 public (DI paramı public olunca property de public olur)
    private platform: Platform,
    private localStorageService: LocalStorageService,
    public translate: TranslateService,
    private auth: AuthService,
    private renderer: Renderer2,
    private languageService: LanguageService,
    @Inject(DOCUMENT) private document: Document,
    private authGuard: AuthGuard,
    private searchService: SearchService,
    private api: ApiService,
    private userDetailService: UserDetailService,
    private authService: AuthService,
    private menuStateService: MenuStateService,
    private modalController: ModalController,
    private dashboardService: DashboardService,
    private themeService: ThemeService,
    private roleManagementService: RoleManagementService,
    private alertService: AlertService,
  ) {
    this.menuCtrl.enable(true, 'main');

    // Menü durumunu takip et
    this.menuCtrl.get('main').then((menu) => {
      if (menu) {
        menu.addEventListener('ionDidOpen', () => {
          this.isMenuOpen = true;
        });
        menu.addEventListener('ionDidClose', () => {
          this.isMenuOpen = false;
        });
      }
    });

    // Subscribe to auth changes; load dashboards for menu once user is authenticated.
    this.auth.user$.subscribe((user) => {
      const wasAuthenticated = this.isAuthenticated;
      this.isAuthenticated = !!user;
      this.profileUser = user;

      if (!user) {
        // Kullanıcı çıkış yaptı — menüyü hemen temizle
        this.menuItems = [];
        this.activeMenuItemsSorted = [];
        this.dynamicDashboardItems = [];
        this._lastPermissions = undefined;
        this.menuLoading = false;
        this.dashboardMenuRetries = 0;
        return;
      }

      // If user just became authenticated (login or token ready), refresh dashboard menu
      if (this.isAuthenticated && !wasAuthenticated) {
        // Menüyü hemen loading moduna al — eski kullanıcı menüsünü gizle
        this.menuItems = [];
        this.activeMenuItemsSorted = [];
        this.dynamicDashboardItems = [];
        this._lastPermissions = undefined;
        this.menuLoading = true;
        // Small debounce to allow other services to initialize
        setTimeout(() => {
          this.loadDashboardsToMenu();
          this.loadActiveMenuOrderAndApplyPermissions();
        }, 300);
      }
    });

    // Subscribe to menu order changes from admin interface
    this.roleManagementService.menuOrderChanged$.subscribe(() => {
      this.loadActiveMenuOrderAndApplyPermissions();
    });

    this.platform.ready().then(() => {
      this.showMenuAndActiveRoute(); // ✅ diğer sayfaların çağırdığı metod mevcut
      this.initTheme();
      this.initTranslate();
      this.onResize(); // İlk ekran boyutu kontrolü
      // NOT: loadDashboardsToMenu burada çağrılmıyor.
      // user$ subscription zaten menuLoading=true set ettikten sonra çağırıyor.

      // AsisComponent global widget — uygulama açıldığında bir kez başlat
      if (window.AsisComponent && !window.__asisInstance) {
        new window.AsisComponent({});
      }
    });

    this.translate.addLangs(['tr', 'en', 'ar']);
    this.translate.setDefaultLang('tr');
    this.selectedLanguage = this.languageService.selectedLanguage;
    this.languageService.languageChange$.subscribe(
      (lang) => (this.selectedLanguage = lang)
    );
    this.translate.use(this.selectedLanguage);
    this.setDirection(this.selectedLanguage);

    this.router.events.subscribe((event: RouterEvent) => {
      if (event instanceof NavigationEnd) this.handleNavigation(event);
    });

    this.currentRoute = this.router.url;
    this.activeUrl = (this.router.url || '').split('?')[0];

    window.addEventListener('toggleSidebar', () => this.toggleMenu());

    // Search bar visibility control
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.showSearchbar =
          event.urlAfterRedirects.includes('/report-gallery');
      });

    loadMessages(trMessages);
    locale('tr');

    // Ensure any DevExpress evaluation/license overlay is removed early and
    // remove/observe future insertions (some libraries re-insert the element).
    try {
      this.removeExistingDxLicenses();
      this.observeDxLicenseInsertions();
    } catch (err) {
      console.error('Failed to initialize dx-license removal:', err);
    }
  }

  /** === DİĞER SAYFALARIN ARADIĞI: MENÜ GÖRÜNÜRLÜĞÜ/ACTIVE URL === */
  public showMenuAndActiveRoute() {
    this.router.events.subscribe((event: RouterEvent) => {
      if (event instanceof NavigationEnd) {
        const url = event.urlAfterRedirects || event.url;
        this.showMenu = !['/login', '/', '/forget-password'].includes(url);
        this.activeUrl = (url || '').split('?')[0];
      }
    });
  }

  /** === DİĞER SAYFALARIN ARADIĞI: HESAP BİLGİLERİ === */
  public getAccount() {
    this.auth
      .getAllAccount()
      .pipe(first())
      .subscribe({
        next: (data) => {
          this.environment.user = data;
          this.auth.userDetail$.subscribe((detail) => {
            this.userDetail = detail;
            this.authService.userProfilePicture$.subscribe((pic) => {
              this.userProfilePictureBase64 = pic?.base64Image || null;
            });
          });
        },
        error: (e) => console.log(e),
      });
  }

  /** ROUTE HANDLER */
  private handleNavigation(event: NavigationEnd) {
    const url = event.urlAfterRedirects || event.url || '';
    this.activeUrl = url.split('?')[0];
    this.currentRoute = this.activeUrl;
    
    // Dashboard ve workbooks sayfalarında menüyü küçült
    if (this.activeUrl.includes('/workbooks-menu-form') || this.activeUrl.includes('/dashboard-definitions')) {
      this.isMenuCollapsed = true;
      this.showMenu = true; // Menüyü göster ama küçült
    } else {
      // Diğer sayfalarda menüyü normal boyutta göster
      this.isMenuCollapsed = false;
      this.showMenu = !['/login', '/', '/forget-password'].includes(url);
    }
    
    // MenuStateService'i de güncelle
    this.menuStateService.setCollapsed(this.isMenuCollapsed);
  }

  /** MENÜ KONTROL: Menüyü kapat ama showMenu'yu koru */
  public hideMenu() {
    console.log('Menü kapatılıyor...');
    this.menuCtrl.close('main');
    this.isMenuOpen = false;
    // Mobil cihazlarda body scroll'unu geri aç
    if (this.isMobile) {
      document.body.style.overflow = '';
    }
  }

  /** Sadece menüyü kapat, header'ı etkileme */
  public closeMenuOnly() {
    console.log('Menü sadece kapatılıyor...');
    this.menuCtrl.close('main');
    this.isMenuOpen = false; // Desktop'ta menü durumunu güncelle
    // Mobil cihazlarda body scroll'unu geri aç
    if (this.isMobile) {
      document.body.style.overflow = '';
    }
  }
  public openMenu() {
    setTimeout(() => this.menuCtrl.open('main'));
    this.isMenuOpen = true; // Desktop'ta menü durumunu güncelle
    // Mobil cihazlarda body scroll'unu engelle
    if (this.isMobile) {
      document.body.style.overflow = 'hidden';
    }
  }
  public toggleMenu() {
    // showMenu durumunu koru, sadece menüyü toggle et
    if (this.isMobile) {
      this.menuCtrl.toggle('main');
    } else {
      // Desktop'ta menü durumunu kontrol et
      this.menuCtrl.isOpen('main').then((isOpen) => {
        if (isOpen) {
          this.menuCtrl.close('main');
          this.isMenuOpen = false;
        } else {
          this.menuCtrl.open('main');
          this.isMenuOpen = true;
        }
      });
    }
  }

  /** YARDIMCI */
  isActive(route: string): boolean {
    return (
      !!route && !!this.currentRoute && this.currentRoute.startsWith(route)
    );
  }

  onTreeViewSelectionChanged(e: any) {
    // DX TreeView fires this event for both selection AND deselection.
    // Only act when the item is being selected, not when it is being deselected.
    if (!e?.node?.selected) return;

    const item = e?.itemData;
    if (item?.comingSoon) {
      this.alertService.showToast('Bu özellik kilitlidir. Yöneticinizle irtibata geçin.', 'warning', 'top-end', 3500);
      return;
    }
    if (item?.externalUrl) {
      window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
      if (this.isMobile) {
        this.menuCtrl.close('main');
        document.body.style.overflow = '';
      }
      return;
    }
    if (item?.route) {
      // Navigate with query params if they exist
      if (item.queryParams) {
        this.router.navigate([item.route], { queryParams: item.queryParams });
      } else {
        this.router.navigate([item.route]);
      }
      
      if (this.isMobile) {
        this.menuCtrl.close('main');
        // Mobil cihazlarda body scroll'unu geri aç
        document.body.style.overflow = '';
      }
    }
  }

  onCollapsedMenuItemClick(item: any) {
    if (item?.comingSoon) {
      this.alertService.showToast('Bu özellik kilitlidir. Yöneticinizle irtibata geçin.', 'warning', 'top-end', 3500);
      return;
    }
    if (item?.externalUrl) {
      window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (item?.route) {
      // Navigate with query params if they exist
      if (item.queryParams) {
        this.router.navigate([item.route], { queryParams: item.queryParams });
      } else {
        this.router.navigate([item.route]);
      }
    } else if (item?.items) {
      // Alt menü varsa genişlet
      this.isMenuCollapsed = false;
    }
  }

  /** Mevcut Keycloak oturumunu kullanarak Open WebUI'yi yeni sekmede SSO ile açar.
   *  Heralp backend'i imzalı token üretir; nginx bunu doğrulayıp Open WebUI'ye
   *  X-Webui-User-Email header'ı enjekte eder → otomatik login. */
  openChatbotSso() {
    this.roleManagementService.getChatbotSsoToken().subscribe({
      next: (result: { token: string; url: string }) => {
        const url = `${result.url}?t=${encodeURIComponent(result.token)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
      },
      error: () => {
        // Fallback: direkt Open WebUI login sayfası
        window.open('https://ai.heralp.local/oauth/oidc/login', '_blank', 'noopener,noreferrer');
      },
    });
  }

  /** ARAMA (üst bar için) */
  onSearch(event: any) {
    const term = event?.detail?.value ?? '';
    this.searchService.updateTerm(term);
  }

  /** DİL/TEMA */
  changeLanguage(lang: string) {
    this.selectedLanguage = lang;
    this.translate.use(lang);
    this.setDirection(lang);
    
    // Dropdown'ı yenile (tema adlarının çevirisi için)
    setTimeout(() => {
      // availableThemes referansını güncelle (çeviriler değişdi)
      this.availableThemes = [...THEME_PRESETS];
    }, 100);
  }

  initTranslate(lang?: string) {
    let resolved =
      (lang && lang.trim()) ||
      this.localStorageService.getStore('language') ||
      this.translate.getBrowserLang() ||
      'tr';
    const supported = ['tr', 'en', 'ar'];
    if (!supported.includes(resolved)) resolved = 'tr';

    moment.locale(resolved);
    this.translate.setDefaultLang(resolved);
    this.translate.use(resolved);
    this.localStorageService.setStore('language', resolved);
    this.selectedLanguage = resolved;
    this.setDirection(resolved);
  }

  private setDirection(lang: string) {
    this.dir = lang === 'ar' ? 'rtl' : 'ltr';
    this.renderer.setAttribute(this.document.documentElement, 'dir', this.dir);
  }

  /**
   * Remove any existing dx-license nodes (different possible selectors)
   */
  private removeExistingDxLicenses(): void {
    try {
      const doc = this.document as Document;
      const selectors = ['dx-license', '.dx-license', '#dx-license', '[data-dx-license]'];
      selectors.forEach((s) => {
        Array.from(doc.querySelectorAll(s)).forEach((n) => {
          try {
            (n as HTMLElement).remove();
          } catch (e) {
            // ignore
          }
        });
      });
    } catch (err) {
      console.error('removeExistingDxLicenses error:', err);
    }
  }

  /**
   * Observe DOM mutations and remove any dx-license nodes as they appear.
   * This helps when a library keeps re-adding the element at runtime.
   */
  private observeDxLicenseInsertions(): void {
    try {
      const doc = this.document as Document;
      const observeTarget = doc.documentElement || doc.body || window.document;
      const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          // handle added nodes
          for (const node of Array.from(m.addedNodes)) {
            try {
              if (!(node instanceof HTMLElement)) continue;

              const el = node as HTMLElement;
              // direct match
              if (
                el.matches('dx-license') ||
                el.matches('.dx-license') ||
                el.matches('#dx-license') ||
                el.hasAttribute('data-dx-license')
              ) {
                el.remove();
                continue;
              }

              // search inside subtree
              const found = el.querySelectorAll('dx-license, .dx-license, #dx-license, [data-dx-license]');
              if (found && found.length) {
                Array.from(found).forEach((n) => {
                  try {
                    (n as HTMLElement).remove();
                  } catch (e) {
                    // ignore
                  }
                });
              }
            } catch (e) {
              // swallow individual node errors
            }
          }
        }
      });

      observer.observe(observeTarget, { childList: true, subtree: true });
      // expose for debugging in console if needed
      try {
        (window as any).__dxLicenseObserver = observer;
      } catch (_) {}
    } catch (err) {
      console.error('observeDxLicenseInsertions error:', err);
    }
  }

  checkDarkSystem(): boolean {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  toggleDarkMode(on: boolean) {
    if (this.checkDarkSystem()) {
      document.body.classList.toggle('dark', on);
      document.body.setAttribute('data-theme', on ? 'dark' : 'light');
    }
    this.localStorageService.setStore('darkModeActivated', JSON.stringify(on));
    this.darkModeActivated = on;
  }

  initTheme() {
    const saved = this.localStorageService.getStore('darkModeActivated');
    this.darkModeActivated = saved ? saved !== 'false' : false;
    this.toggleDarkMode(this.darkModeActivated);
    
    // Initialize DevExpress theme
    this.themeService.restoreLastPreset();
    
    // Subscribe to theme changes
    this.themeService.currentPreset$.subscribe((preset) => {
      if (preset) {
        this.selectedTheme = preset;
      }
    });
  }

  /**
   * Change DevExpress theme
   * @param themeId The ID of the theme to apply
   */
  changeTheme(themeId: string): void {
    console.log('🎨 Tema değiştiriliyor:', themeId);
    
    // Debug: SVG rect'leri kontrol et
    setTimeout(() => {
      const dashboard = document.querySelector('.dx-dashboard-viewer');
      if (dashboard) {
        const rects = dashboard.querySelectorAll('svg rect');
        console.log('📊 SVG rect sayısı:', rects.length);
        
        rects.forEach((rect, index) => {
          const fill = (rect as SVGElement).getAttribute('fill');
          const computedFill = window.getComputedStyle(rect as SVGElement).fill;
          console.log(`  [${index}] fill="${fill}" computed="${computedFill}"`);
        });
      }
    }, 1000);
    
    this.themeService.applyPreset(themeId, false);
    // selectedTheme will be updated via subscription in initTheme
  }

  /** LAYOUT */
  @HostListener('window:resize') checkScreenSize() {}

  /** GÖRSEL */
  getProfileImage(): string {
    const url = this.userProfilePictureBase64?.trim();
    return url && !url.includes('null') && !url.includes('/undefined')
      ? url
      : 'assets/images/avatar2.png';
  }
  onImageError(e: Event) {
    (e.target as HTMLImageElement).src = 'assets/images/avatar2.png';
  }

  /** RESPONSIVE */
  @HostListener('window:resize')
  onResize() {
    this.isMobile = window.innerWidth < 768;
  }

  /** SIDEBAR TOGGLE */
  toggleSidebar() {
    if (this.isMobile) {
      // mobil: overlay menüyü aç/kapat
      this.menuCtrl.toggle('main');
    } else {
      // desktop: menüyü daralt/genişlet
      this.isMenuCollapsed = !this.isMenuCollapsed;
      this.menuCtrl.open('main'); // Menüyü her zaman açık tut
    }
  }

  /** PROFİL MENÜ */
  toggleProfileMenu() {
    this.isProfileMenuOpen = !this.isProfileMenuOpen;
  }

  @HostListener('document:click', ['$event'])
  closeProfileMenu(event: Event) {
    const inside =
      (event.target as HTMLElement).closest('.submenu-wrapper') ||
      (event.target as HTMLElement).closest('.profile-photo') ||
      (event.target as HTMLElement).closest('.topbar-profile');
    if (!inside) this.isProfileMenuOpen = false;
  }

  handleMenuItemClick(item: any) {
    if (item.action === 'logout') {
      this.logout();
      this.isProfileMenuOpen = false;
      return;
    }
    if (item.action === 'changePassword') {
      this.openChangePasswordModal();
      this.isProfileMenuOpen = false;
      return;
    }
    if (item.link) {
      this.router.navigate([item.link]);
      this.isProfileMenuOpen = false;
    }
  }

  async openChangePasswordModal() {
    const modal = await this.modalController.create({
      component: ChangePasswordModalPage,
      cssClass: 'full-modal',
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();
    if (role === 'success') {
      this.logout();
    }
  }

  logout() {
    this.auth.logout();
    this.router.navigate(['/login']);
  }

  /**
   * Load dashboards from API and populate menu items
   */
  loadDashboardsToMenu() {
    this.dashboardService.getDashboardsGrid(0, 20, 'createdDt,desc').subscribe({
      next: (dashboards: Dashboard[]) => {
        if (dashboards && dashboards.length > 0) {
          this.dynamicDashboardItems = dashboards.map((dashboard) => ({
            id: `dashboard-${dashboard.id}`,
            text: dashboard.name || `Dashboard ${dashboard.id}`,
            icon: 'stats-chart-outline',
            route: '/dashboard-definitions',
            queryParams: { dashboardId: dashboard.id, dashboardName: dashboard.name },
          }));
          // Rebuild menu so dashboards appear alongside static reporting items
          this.rebuildCurrentMenu();
          this.dashboardMenuRetries = 0;
        } else if (this.dashboardMenuRetries < this.DASHBOARD_MENU_MAX_RETRIES) {
          this.dashboardMenuRetries++;
          const delay = 500 * this.dashboardMenuRetries;
          setTimeout(() => this.loadDashboardsToMenu(), delay);
        } else {
          this.dashboardMenuRetries = 0;
        }
      },
      error: () => {},
    });
  }

  /** Rebuilds the menu using the last known permission state and DB order.
   *  Called when dynamic dashboards finish loading after the initial full rebuild.
   *  Guard: if menuLoading is still true, permissions haven't arrived yet —
   *  rebuildFullMenu will include dashboards when it fires. */
  private rebuildCurrentMenu() {
    if (this.menuLoading) return;
    this.rebuildFullMenu(this._lastPermissions);
  }

  /**
   * Load active menu items from DB and rebuild menu with permission filtering.
   * This replaces loadAndApplyMenuPermissions and also loads the active menu order
   * from the API to sort the sidebar correctly.
   */
  loadActiveMenuOrderAndApplyPermissions() {
    // First load active menu items sorted by DB
    this.roleManagementService.getActiveMenuItems().subscribe({
      next: (items) => {
        this.activeMenuItemsSorted = items;
        // Then load permissions and rebuild menu with sort order
        this.loadAndApplyMenuPermissions();
      },
      error: () => {
        // If active menu items can't load, just apply permissions
        this.loadAndApplyMenuPermissions();
      },
    });
  }

  /**
   * Load the current user's RBAC permissions and rebuild the menuItems list,
   * filtering out items the user does not have canView permission on.
   * ADMIN users (ROLE_ADMIN in JHipster authorities) bypass the filter.
   */
  loadAndApplyMenuPermissions() {
    this.roleManagementService.loadMyPermissions().subscribe({
      next: (permissions) => {
        const isAdmin = this.profileUser?.authorities?.includes('ROLE_ADMIN');
        // If no permissions are defined at all, show full menu (graceful degradation)
        const hasAnyPerm = Object.keys(permissions).length > 0;
        if (isAdmin || !hasAnyPerm) {
          // Admin or no permissions configured — show full menu
          this._lastPermissions = undefined;
          this.rebuildFullMenu();
          return;
        }
        // Filter menu items by canView and cache for subsequent dashboard reloads
        this._lastPermissions = permissions;
        this.rebuildFullMenu(permissions);
      },
      error: () => {
        // Permissions could not be loaded — show full menu (fail-open)
        this._lastPermissions = undefined;
        this.rebuildFullMenu();
      },
    });
  }

  private rebuildFullMenu(permissions?: { [key: string]: any }) {
    const canView = (key: string) => !permissions || !!permissions[key]?.canView;

    // Build a sort map from active menu items (DB sort order)
    const sortMap = new Map<string, number>();
    this.activeMenuItemsSorted.forEach((item) => {
      sortMap.set(item.itemKey || item.id, item.sortOrder ?? 999);
    });

    // Helper to sort items by sortMap, then by existing order
    const sortByDbOrder = (items: any[]): any[] => {
      return items.sort((a, b) => {
        const aOrder = sortMap.get(a.id) ?? 999;
        const bOrder = sortMap.get(b.id) ?? 999;
        return aOrder - bOrder;
      });
    };

    // Helper: get DB-stored label/icon for a menu key, fallback to hardcoded defaults
    const dbItem = (key: string) => this.activeMenuItemsSorted.find(i => i.itemKey === key);
    const label = (key: string, fallback: string) => dbItem(key)?.label || fallback;
    const icon  = (key: string, fallback: string) => dbItem(key)?.icon  || fallback;

    // Helper: build a parent group — auto-expand when there is only one visible child
    const mkGroup = (id: string, txt: string, icn: string, children: any[]) => ({
      id, text: txt, icon: icn, items: children, expanded: children.length === 1,
    });

    const allItems: any[] = [
      ...(canView('home') ? [{ id: 'home', text: label('home', 'Ana Sayfa'), icon: icon('home', 'home-outline'), route: '/dashboard-grid' }] : []),
      ...(canView('dashboards')
        ? [this.dynamicDashboardItems.length > 0
            ? mkGroup('dashboards', label('dashboards', 'Dashboardlar'), icon('dashboards', 'stats-chart-outline'), this.dynamicDashboardItems)
            : { id: 'dashboards', text: label('dashboards', 'Dashboardlar'), route: '/dashboard-definitions', icon: icon('dashboards', 'stats-chart-outline') }]
        : []),
    ];

    // Raporlama & Analitik — sadece Raporlar ve Çalışma Kitapları
    const reportingChildren = sortByDbOrder([
      ...(canView('reports') ? [{ id: 'reports', text: label('reports', 'Raporlar'), route: '/report-gallery', icon: icon('reports', 'document-text-outline') }] : []),
      ...(canView('workbooks') ? [{ id: 'workbooks', text: label('workbooks', 'Çalışma Kitapları'), route: '/workbooks-menu', icon: icon('workbooks', 'library-outline') }] : []),
    ]);
    if (canView('reporting') || reportingChildren.length > 0) {
      allItems.push(mkGroup('reporting', label('reporting', 'Raporlama & Analitik'), icon('reporting', 'bar-chart-outline'), reportingChildren));
    }

    // Veri Yönetimi
    const dataChildren = sortByDbOrder([
      ...(canView('data-sources') ? [{ id: 'data-sources', text: label('data-sources', 'Veri Kaynakları'), route: '/data-sources-menu', icon: icon('data-sources', 'cloud-outline') }] : []),
      ...(canView('data-store') ? [{ id: 'data-store', text: label('data-store', 'Veri Deposu'), route: '/data-store-menu', icon: icon('data-store', 'server-outline') }] : []),
      ...(canView('data-prep') ? [{ id: 'data-prep', text: label('data-prep', 'GraphQL'), icon: icon('data-prep', 'construct-outline'), externalUrl: 'http://10.10.1.50:30082/browser/' }] : []),
      
    ]);
    if (canView('data-management') || dataChildren.length > 0) {
      allItems.push(mkGroup('data-management', label('data-management', 'Veri Yönetimi'), icon('data-management', 'server-outline'), dataChildren));
    }

    // Yapay Zeka Geliştirme
    const aiDevChildren = sortByDbOrder([
      ...(canView('model-dev') ? [{ id: 'model-dev', text: label('model-dev', 'Model Geliştirme'), icon: icon('model-dev', 'hardware-chip-outline'), externalUrl: 'https://kubeflow.local/' }] : []),
      ...(canView('experiments') ? [{ id: 'experiments', text: label('experiments', 'Deneyler'), icon: icon('experiments', 'flask-outline'), externalUrl: 'https://mlflow.local/' }] : []),
    ]);
    if (canView('ai-dev') || aiDevChildren.length > 0) {
      allItems.push(mkGroup('ai-dev', label('ai-dev', 'Yapay Zeka Geliştirme'), icon('ai-dev', 'hardware-chip-outline'), aiDevChildren));
    }

    // Kurumsal AI Asistan, Doküman Yönetimi, Bilgi Havuzu — üst seviye öğeler
    if (canView('chatbot')) {
      allItems.push({ id: 'chatbot', text: label('chatbot', 'Kurumsal AI Asistan'), icon: icon('chatbot', 'chatbubbles-outline'), externalUrl: 'https://ai.heralp.local/' });
    }
    if (canView('documents')) {
      allItems.push({ id: 'documents', text: label('documents', 'Doküman Yönetimi'), icon: icon('documents', 'folder-open-outline'), route: '/document-management' });
    }
    if (canView('knowledge')) {
      allItems.push({ id: 'knowledge', text: label('knowledge', 'Bilgi Havuzu'), icon: icon('knowledge', 'book-outline'), route: '/knowledge-base' });
    }

    // Model Yönetimi
    const modelChildren = sortByDbOrder([
      ...(canView('models') ? [{ id: 'models', text: label('models', 'Model Kütüphanesi'), icon: icon('models', 'cube-outline'), externalUrl: 'https://litellm.local/' }] : []),
      ...(canView('model-monitoring') ? [{ id: 'model-monitoring', text: label('model-monitoring', 'Model İzleme'), icon: icon('model-monitoring', 'pulse-outline'), externalUrl: 'http://10.10.1.50:8058/' }] : []),
    ]);
    if (canView('model-mgmt') || modelChildren.length > 0) {
      allItems.push(mkGroup('model-mgmt', label('model-mgmt', 'Model Yönetimi'), icon('model-mgmt', 'cube-outline'), modelChildren));
    }

    // Karar Yönetimi
    const decisionChildren = sortByDbOrder([
      ...(canView('rules') ? [{ id: 'rules', text: label('rules', 'İş Kuralları'), icon: icon('rules', 'git-merge-outline'), externalUrl: 'https://agent.heralp.local/?key=sk-RP5ootO90v5y367vzo' }] : []),
      ...(canView('decision-flows') ? [{ id: 'decision-flows', text: label('decision-flows', 'Karar Akışları'), icon: icon('decision-flows', 'git-network-outline'), externalUrl: 'https://n8n.local/' }] : []),
    ]);
    if (canView('decision-mgmt') || decisionChildren.length > 0) {
      allItems.push(mkGroup('decision-mgmt', label('decision-mgmt', 'Karar Yönetimi'), icon('decision-mgmt', 'git-merge-outline'), decisionChildren));
    }

    // Entegrasyon
    const integChildren = sortByDbOrder([
      ...(canView('api-services') ? [{ id: 'api-services', text: label('api-services', 'API Servisleri'), icon: icon('api-services', 'code-slash-outline'), comingSoon: true }] : []),
      ...(canView('connectors') ? [{ id: 'connectors', text: label('connectors', 'Veri Bağlantıları'), icon: icon('connectors', 'link-outline'), comingSoon: true }] : []),
    ]);
    if (canView('integration') || integChildren.length > 0) {
      allItems.push(mkGroup('integration', label('integration', 'Entegrasyon'), icon('integration', 'link-outline'), integChildren));
    }

    // İzleme
    const monitorChildren = sortByDbOrder([
      ...(canView('metrics') ? [{ id: 'metrics', text: label('metrics', 'Sistem Metrikleri'), icon: icon('metrics', 'speedometer-outline'), route: '/system-metrics' }] : []),
    ]);
    if (canView('monitoring') || monitorChildren.length > 0) {
      allItems.push(mkGroup('monitoring', label('monitoring', 'İzleme'), icon('monitoring', 'pulse-outline'), monitorChildren));
    }

    // AI Çağrı Analizi (always visible — preserved feature)
    if (canView('ai-call-analysis') || !permissions) {
      allItems.push({ id: 'ai-call-analysis', text: label('ai-call-analysis', 'AI Çağrı Analizi'), description: 'WhisperX & Gemma AI çağrı arşivi', icon: icon('ai-call-analysis', 'mic-outline'), route: '/ai-call-analysis' });
    }

    // Yönetim — tek link, tüm tab'lar sayfa içinde
    if (canView('administration') || canView('user-mgmt') || canView('roles-mgmt') || canView('system-config') || !permissions) {
      allItems.push({ id: 'definitions', text: label('definitions', 'Yönetim'), icon: icon('definitions', 'cog-outline'), route: '/definations' });
    }

    // Apply final sort order from DB to all items
    this.menuItems = [...sortByDbOrder(allItems)];
    this.menuLoading = false;
  }
}
